import "core-js/modules/web.dom-collections.iterator.js";
import React, { forwardRef, useState, useImperativeHandle, useMemo, useCallback } from 'react';
import { styled } from '@pixel/core';
import { useCKEditor, registerEditorEventHandler, CKEditorEventAction } from "ckeditor4-react";
import './styles.css';
const EditorWrapper = styled('div')(() => ({
  '& .cke_combo_text': {
    width: 'auto',
    minWidth: '80px'
  },
  '& .cke_combo__mergecodes .cke_combo_text': {
    minWidth: '100px'
  }
}));
export const Editor = /*#__PURE__*/forwardRef((props, forwardRef) => {
  const {
    variant = "standard",
    initialValue,
    configuration,
    disabled = false,
    extraConfig = {},
    onPluginsLoaded = _payload => {},
    onChange = _payload => {}
  } = props;
  let attached = false;
  const [initValue, setInitValue] = useState(initialValue);
  const [element, setElement] = useState(null);
  const toolbarConfiguration = useMemo(() => {
    var _props$configuration;
    return {
      versionCheck: false,
      //remove after lts upgrade
      toolbarGroups: [...props.configuration.toolbar, props.allowFullscreen ? {
        name: 'tools'
      } : undefined].filter(item => Boolean(item)),
      extraPlugins: 'filebrowser,font,colorbutton,justify',
      disableNativeSpellChecker: false,
      removeDialogTabs: 'image:advanced;link:advanced',
      removePlugins: 'liststyle,tableselection,tabletools,scayt,menubutton,contextmenu,elementspath',
      removeButtons: "Anchor,SpecialChar,Subscript,Superscript".concat(props.configuration.removeButtons || ''),
      // TODO: add removeButtons prop: Styles,Block Quote
      forcePasteAsPlainText: true,
      filebrowserUploadUrl: props.imageUploadURL ? props.imageUploadURL : undefined,
      filebrowserLinkUrl: props.imageUploadURL ? props.imageUploadURL : undefined,
      ...extraConfig,
      ...(Boolean(props === null || props === void 0 || (_props$configuration = props.configuration) === null || _props$configuration === void 0 ? void 0 : _props$configuration.extraConfig) ? props.configuration.extraConfig : {})
    };
  }, [props.configuration, props.configuration.toolbar, props.configuration.removeButtons, props.configuration.extraConfig, props.imageUploadURL, props.allowFullscreen, extraConfig]);
  const handleInstanceReady = payload => {
    payload.editor.setData(initValue);
    payload.editor.setReadOnly(disabled);
    switch (props.variant) {
      case "advanced":
        // prevent adding new line for pre tag after open
        payload.editor.dataProcessor.writer.setRules('pre', {
          indent: false,
          breakBeforeOpen: false,
          breakAfterOpen: false,
          breakBeforeClose: false,
          breakAfterClose: false
        });
    }
    if (payload.editor && typeof props.onLoad === 'function') {
      props.onLoad();
    }
  };
  const handleFileUploadRequest = useCallback(payload => {
    if (payload.editor && typeof props.onFileUploadRequest === 'function') {
      props.onFileUploadRequest(payload);
    }
  }, [props.onFileUploadRequest]);
  const handleFileUploadResponse = useCallback(payload => {
    if (payload.editor && typeof props.onFileUploadResponse === 'function') {
      props.onFileUploadResponse(payload);
    }
  }, [props.onFileUploadResponse]);
  const handleBeforeLoad = useCallback(CKEDITOR => {
    !attached && CKEDITOR.on('dialogDefinition', event => {
      const dialogName = event.data.name;
      const dialogDefinition = event.data.definition;
      if (dialogName === 'link') {
        dialogDefinition.removeContents('upload');
      }
    });
    !attached && CKEDITOR.addCss(".cke_editable{font-size: 13px; font-family: Helvetica, Roboto, Calibri, Arial, Verdana, sans-serif;}");
    attached = true;
  }, [attached]);
  const handlePluginsLoaded = payload => {
    if (typeof onPluginsLoaded === 'function') {
      onPluginsLoaded(payload);
    }
  };
  const handleChange = payload => {
    if (typeof onChange === 'function') {
      setInitValue(payload.editor.getData());
      onChange(payload);
    }
  };
  const handleEventDispatch = _ref => {
    let {
      type,
      payload
    } = _ref;
    // console.log( 'dispatchEvent', type, payload );
    if (type === CKEditorEventAction.instanceReady) {
      handleInstanceReady(payload);
    }
    if (type === CKEditorEventAction.pluginsLoaded) {
      handlePluginsLoaded(payload);
    }
    if (type === CKEditorEventAction.change) {
      handleChange(payload);
    }
    if (type === CKEditorEventAction.beforeLoad) {
      handleBeforeLoad(payload);
    }
    if (type === CKEditorEventAction.fileUploadRequest) {
      handleFileUploadRequest(payload);
    }
    if (type === CKEditorEventAction.fileUploadResponse) {
      handleFileUploadResponse(payload);
    }
  };
  const {
    editor,
    status,
    error,
    loading
  } = useCKEditor({
    config: toolbarConfiguration,
    debug: true,
    dispatchEvent: e => handleEventDispatch(e),
    //editorUrl: 'https://your-website.example/ckeditor/ckeditor.js',
    element: element,
    type: 'classic',
    subscribeTo: ['beforeLoad', 'instanceReady', 'pluginsLoaded', 'change', 'focus', 'blur', 'fileUploadRequest', 'fileUploadResponse']
  });
  const getValue = () => {
    if (editor) {
      return editor.getData();
    }
    return null;
  };
  const clearValue = () => {
    if (editor) {
      editor.setData('');
    }
  };
  const getValueText = () => {
    if (editor !== null && editor !== void 0 && editor.document) {
      var _editor$document$getB;
      return (_editor$document$getB = editor.document.getBody()) === null || _editor$document$getB === void 0 ? void 0 : _editor$document$getB.getText();
    }
    return null;
  };
  const setValue = (value, callback) => {
    if (editor) {
      editor.setData(value, callback);
    } else {
      //setTempValue(value);
    }
  };
  useImperativeHandle(forwardRef, () => ({
    getValueText: getValueText,
    getValueHTML: getValue,
    setValue: setValue,
    clear: clearValue,
    editor: editor
  }));

  //This is CKEditor4's way of handling the events.. 
  //they cache the calls on their end so this cleans them up and allows you to have fresh props
  React.useEffect(() => {
    if (editor) {
      const cleanup = registerEditorEventHandler({
        editor,
        evtName: 'instanceReady',
        handler: handleInstanceReady,
        priority: 0
      });
      return cleanup;
    }
  }, [editor, initValue]);
  return /*#__PURE__*/React.createElement(EditorWrapper, null, /*#__PURE__*/React.createElement("div", {
    ref: setElement
  }));
});
export default Editor;